import { useEffect, useState } from 'react';
import { action } from './api.ts';

/**
 * The ERP PRO app for Android shows these same pages and adds what a browser cannot do: alerts
 * with the phone in a pocket, files saved to Downloads, the screen kept on. It hands the pages
 * `window.ErpNative`; in a browser it is missing and everything here does nothing.
 */
export interface NativeInfo {
  platform: 'android';
  version: string;
  device: string;
  model: string;
  os: string;
  server: string;
  alerts: boolean;
  scope: 'mine' | 'location';
  location: string;
  /** Android lets the app show notifications. */
  notifications: boolean;
  /** Alerts are on, the person is signed in and notifications are allowed: the phone is listening. */
  listening: boolean;
  /** The phone's battery saver leaves the app alone (alerts arrive on time). */
  battery_saver_free: boolean;
  keep_awake: boolean;
}

interface Bridge {
  info(): string;
  session(token: string, user: string, name: string): void;
  signedOut(): void;
  alerts(on: boolean, scope: string, location: string): void;
  allowNotifications(): void;
  notificationSettings(): void;
  batterySettings(): void;
  testAlert(): void;
  keepAwake(on: boolean): void;
  bars(top: string, bottom: string, dark: boolean): void;
  saveFile(name: string, mime: string, base64: string): boolean;
  changeServer(): void;
}

declare global {
  interface Window {
    ErpNative?: Bridge;
    /** The phone's back button: closes an open dialog or menu first. */
    __erpBack?: () => boolean;
    /** A notification opens a page of the app without loading it again. */
    __erpOpen?: (path: string) => boolean;
  }
}

const bridge = (): Bridge | undefined => (typeof window === 'undefined' ? undefined : window.ErpNative);
export const inApp = () => !!bridge();

export function nativeInfo(): NativeInfo | null {
  const b = bridge();
  if (!b) return null;
  try {
    return JSON.parse(b.info()) as NativeInfo | null;
  } catch {
    return null;
  }
}

/** What the phone says now; asked again when the app tells the page something changed or it comes back to the front. */
export function useNative(): NativeInfo | null {
  const [info, setInfo] = useState(nativeInfo);
  useEffect(() => {
    if (!bridge()) return;
    const again = () => setInfo(nativeInfo());
    window.addEventListener('erpnative', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('erpnative', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, []);
  return info;
}

const call = (fn: (b: Bridge) => void) => {
  const b = bridge();
  if (b) fn(b);
};

export const native = {
  session: (token: string | null, user: string, name: string) => call((b) => (token ? b.session(token, user, name) : b.signedOut())),
  signedOut: () => call((b) => b.signedOut()),
  alerts: (on: boolean, scope: 'mine' | 'location', location: string | null) => call((b) => b.alerts(on, scope, location ?? '')),
  allowNotifications: () => call((b) => b.allowNotifications()),
  notificationSettings: () => call((b) => b.notificationSettings()),
  batterySettings: () => call((b) => b.batterySettings()),
  testAlert: () => call((b) => b.testAlert()),
  keepAwake: (on: boolean) => call((b) => b.keepAwake(on)),
  changeServer: () => call((b) => b.changeServer()),
  /** Saves a file the page made into the phone's Downloads; false when not in the app or it failed. */
  saveFile(name: string, mime: string, text: string): boolean {
    const b = bridge();
    if (!b) return false;
    const bytes = new TextEncoder().encode(text);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return b.saveFile(name, mime, btoa(bin));
  },
};

/** Tells the server about this phone (Manage → Phones), with its alert settings. */
export async function registerPhone(info: NativeInfo | null = nativeInfo()) {
  if (!info) return;
  await action('mobile.register', {
    device: info.device,
    platform: info.platform,
    model: info.model || null,
    os_version: info.os || null,
    app_version: info.version,
    alerts: info.alerts,
    scope: info.scope,
    location: info.location || null,
  }).catch(() => {});
}

/** #rgb, rgb(…) or rgba(…) as #rrggbb, for the phone's system bars. */
export function hexColour(css: string): string | null {
  const s = css.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) return `#${[...s.slice(1)].map((c) => c + c).join('')}`.toLowerCase();
  const m = /^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(s);
  if (!m) return null;
  return `#${[m[1], m[2], m[3]].map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0')).join('')}`;
}

/** Paints the phone's status bar like the page's top bar and the navigation bar like its bottom. */
export function syncBars() {
  const b = bridge();
  if (!b) return;
  const root = getComputedStyle(document.documentElement);
  const bg = root.getPropertyValue('--bg').trim();
  const surface = hexColour(root.getPropertyValue('--surface')) ?? hexColour(bg) ?? '';
  // A see-through background (alpha 0) means the page's own background shows.
  const pick = (sel: string, fallback: string) => {
    const el = document.querySelector(sel);
    const c = el ? getComputedStyle(el).backgroundColor : '';
    return hexColour(c && c !== 'transparent' && !/,\s*0\)$/.test(c) ? c : fallback) ?? surface;
  };
  const top = pick('.topbar', bg);
  // On a phone the main menu sits at the bottom of the screen.
  const nav = document.querySelector('.topnav');
  const bottom = nav && getComputedStyle(nav).position === 'fixed' ? pick('.topnav', bg) : hexColour(bg) ?? top;
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  };
  b.bars(top, bottom, lum(top) < 128);
}

/** The back button closes the topmost dialog or menu (they all close on Escape). */
export function installBackHandler() {
  window.__erpBack = () => {
    const open = document.querySelector('[role="dialog"], .topnav-sheet, [role="menu"]');
    if (!open) return false;
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    return true;
  };
}
